import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  ReturnRequestFiltersExtended,
  returnService,
} from "~/services/wms/returnService";
import type {
  ReturnInspectionFormData,
  ReturnPutawayFormData,
  ReturnReceiveFormData,
  ReturnRequestFormData,
  ReturnRequestStatus,
} from "~/types/returns";

const QUERY_KEY = "return-requests";
const STATS_QUERY_KEY = "return-statistics";

// ============================================================================
// Return Requests
// ============================================================================

export function useReturnRequests(filters?: ReturnRequestFiltersExtended) {
  return useQuery({
    queryKey: [QUERY_KEY, filters],
    queryFn: () => returnService.getReturnRequests(filters),
  });
}

export function usePendingReturnRequests() {
  return useQuery({
    queryKey: [QUERY_KEY, "pending"],
    queryFn: () => returnService.getReturnRequests({ status: "pending" }),
  });
}

export function useInProgressReturnRequests() {
  return useQuery({
    queryKey: [QUERY_KEY, "in-progress"],
    queryFn: () =>
      returnService.getReturnRequests({
        status: ["receiving", "inspecting", "routing"],
      }),
  });
}

export function useReturnRequest(id: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => returnService.getReturnRequestById(id!),
    enabled: !!id,
  });
}

export function useCreateReturnRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (formData: ReturnRequestFormData) =>
      returnService.createReturnRequest(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] });
    },
  });
}

export function useClaimReturnRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      returnService.claimReturnRequest(id, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.id] });
      queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] });
    },
  });
}

export function useUpdateReturnRequestStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      status,
      additionalData,
    }: {
      id: string;
      status: ReturnRequestStatus;
      additionalData?: {
        completed_at?: string;
        notes?: string;
        internal_notes?: string;
      };
    }) => returnService.updateReturnRequestStatus(id, status, additionalData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.id] });
      queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] });
    },
  });
}

// ============================================================================
// Return Request Items
// ============================================================================

export function useReturnRequestItems(returnRequestId: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, returnRequestId, "items"],
    queryFn: () => returnService.getReturnRequestItems(returnRequestId!),
    enabled: !!returnRequestId,
  });
}

export function useReceiveReturnItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      formData,
      userId,
    }: {
      formData: ReturnReceiveFormData;
      userId: string;
    }) => returnService.receiveReturnItem(formData, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] });
    },
  });
}

// ============================================================================
// Inspections
// ============================================================================

export function useCreateInspection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      formData,
      userId,
    }: {
      formData: ReturnInspectionFormData;
      userId: string;
    }) => returnService.createInspection(formData, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] });
    },
  });
}

export function useRouteInspection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      formData,
      userId,
    }: {
      formData: ReturnPutawayFormData;
      userId: string;
    }) => returnService.routeInspection(formData, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] });
    },
  });
}

// ============================================================================
// Statistics
// ============================================================================

export function useReturnStatistics() {
  return useQuery({
    queryKey: [STATS_QUERY_KEY],
    queryFn: () => returnService.getReturnStatistics(),
    refetchInterval: 30000, // Refetch every 30 seconds
  });
}

// ============================================================================
// Warehouses by Type
// ============================================================================

export function useWarehousesByType(warehouseType: string | undefined) {
  return useQuery({
    queryKey: ["warehouses", "by-type", warehouseType],
    queryFn: () => returnService.getWarehousesByType(warehouseType!),
    enabled: !!warehouseType,
  });
}

// ============================================================================
// Check and Complete
// ============================================================================

export function useCheckAndCompleteReturnRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (returnRequestId: string) =>
      returnService.checkAndCompleteReturnRequest(returnRequestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] });
    },
  });
}
